'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import logoIcon from '@images/navbar/logo_main-nav.svg'

import styles from '@app/[locale]/v1/v1.module.scss'

type JobsHeaderProps = {
  locale: string
}

export default function JobsHeader({ locale }: JobsHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const pathname = usePathname()
  const isJobsPage = pathname?.includes('/jobs')

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      setIsScrolled(currentScrollY > 50)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    // Add class to body when mobile menu is open to push content down
    if (isMenuOpen) {
      document.body.classList.add('mobileMenuOpen')
    } else {
      document.body.classList.remove('mobileMenuOpen')
    }
    return () => {
      document.body.classList.remove('mobileMenuOpen')
    }
  }, [isMenuOpen])

  useEffect(() => {
    // Close mobile menu when screen is resized to desktop size (md breakpoint = 768px)
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMenuOpen) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isMenuOpen])

  const handleLogoClick = () => {
    setIsMenuOpen(false)
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  return (
    <header className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}>
      <div className={styles.headerInner}>
        <Link href={`/${locale}`} className={styles.logoContainer} onClick={handleLogoClick}>
          <Image src={logoIcon} alt="SonicJobs" width={131} height={33} className={styles.logoImage} />
        </Link>

        <nav className={styles.nav}>
          <Link href={`/${locale}`} className={styles.navLink}>
            Home
          </Link>
          <Link href={`/${locale}/jobs`} className={`${styles.navLink} ${isJobsPage ? styles.navLinkActive : ''}`}>
            Browse Jobs
          </Link>
          <Link href={`/${locale}/investors`} className={styles.navLink}>
            Computer Use Agents
          </Link>
        </nav>

        <div style={{ width: '120px', flexShrink: 0 }} aria-hidden="true"></div>

        <button
          className={styles.menuButton}
          type="button"
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 7H20M4 12H20M4 17H20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={`${styles.mobileNav} ${isMenuOpen ? styles.mobileNavOpen : ''}`}>
        <nav className={styles.mobileNavList}>
          <Link href={`/${locale}`} className={styles.mobileNavLink} onClick={() => setIsMenuOpen(false)}>
            Home
          </Link>
          <Link
            href={`/${locale}/jobs`}
            className={`${styles.mobileNavLink} ${isJobsPage ? styles.mobileNavLinkActive : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            Browse Jobs
          </Link>
          <Link href={`/${locale}/investors`} className={styles.mobileNavLink} onClick={() => setIsMenuOpen(false)}>
            Computer Use Agents
          </Link>
        </nav>
      </div>
    </header>
  )
}
